import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client/react';
import type { TabType } from './AppContent.types';
import { registerMockVueMfe } from './AppContent.utils';
import { useTheme } from './useTheme';
import { useFederatedRoutes } from './useFederatedRoutes';
import { useGraphQLSimulation } from './useGraphQLSimulation';
import { useAuthErrorHandler } from '../../hooks/useAuthErrorHandler';
import { useLiveNotifications } from '../../hooks/useLiveNotifications';
import { LOGOUT_MUTATION, GET_CURRENT_USER, client } from '../../graphql/client';
import { store, resetApp } from '../../store/configureStore';
import { setRegion } from "@Components/Common/Region/region.slice";
import { getBrowserRegionDefaults } from '../../utilities/region';

export const useAppContent = () => {
  useAuthErrorHandler();
  useLiveNotifications();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isDevWorkbenchOpen, setIsDevWorkbenchOpen] = useState(false);
  const [isCounterInjected, setIsCounterInjected] = useState(false);
  const [reduxStateStr, setReduxStateStr] = useState('');

  const region = useSelector((state: any) => state.region) || {};
  const fullReduxState = undefined; // Kept from original

  const { theme, toggleTheme } = useTheme();
  const { routes, routesLoading } = useFederatedRoutes();
  const { simulatingQuery, simulatedPayload, simulateGraphQL } = useGraphQLSimulation();

  const initialFetchPolicy = localStorage.getItem("auth_token")
    ? "network-only"
    : "cache-and-network";

  const { data: userData, refetch: refetchCurrentUser } = useQuery<{
    me?: {
      id: string;
      name: string;
      email: string;
      culture?: string;
      currency?: string;
      roles?: { name: string }[];
    };
  }>(GET_CURRENT_USER, {
    errorPolicy: "all",
    fetchPolicy: initialFetchPolicy as any,
  });

  // Sync loaded user preferences to Redux
  useEffect(() => {
    if (userData?.me) {
      const targetCulture = userData.me.culture || getBrowserRegionDefaults().culture;
      const targetCurrency = userData.me.currency || getBrowserRegionDefaults().currency;

      if (region.culture !== targetCulture || region.currency !== targetCurrency) {
        dispatch(setRegion({ culture: targetCulture, currency: targetCurrency }));
      }
    }
  }, [userData, region, dispatch]);

  // Refetch current user when auth token changes
  useEffect(() => {
    const handler = () => {
      try {
        if (refetchCurrentUser) refetchCurrentUser();
      } catch (e) {
        void e;
      }
    };
    window.addEventListener("auth:changed", handler);
    if (localStorage.getItem("auth_token")) {
      handler();
    }
    return () => window.removeEventListener("auth:changed", handler);
  }, [refetchCurrentUser]);

  // Synchronize Redux store inspector string
  useEffect(() => {
    setReduxStateStr(JSON.stringify(fullReduxState, null, 2));
  }, [fullReduxState]);

  // Setup Mock MFEs at runtime on component load
  useEffect(() => {
    registerMockVueMfe();
  }, []);

  const [logoutMutation] = useMutation(LOGOUT_MUTATION, {
    context: { skipPersistedQueries: true },
    onCompleted: () => {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("token_type");
      localStorage.removeItem("google_oauth_connected");
      localStorage.removeItem("google_oauth_just_connected");
      localStorage.removeItem("google_oauth_failed_message");
      client.clearStore().catch(() => {});
      store.dispatch(resetApp());
      window.dispatchEvent(new CustomEvent('auth:changed'));
      navigate("/login", { replace: true });
    },
    onError: (error: any) => {
      console.warn("Logout mutation failed (simulating developer fallback):", error);
      localStorage.removeItem("auth_token");
      localStorage.removeItem("token_type");
      localStorage.removeItem("google_oauth_connected");
      localStorage.removeItem("google_oauth_just_connected");
      localStorage.removeItem("google_oauth_failed_message");
      client.clearStore().catch(() => {});
      store.dispatch(resetApp());
      window.dispatchEvent(new CustomEvent('auth:changed'));
      navigate("/login", { replace: true });
    },
  });

  const handleLogout = () => {
    logoutMutation();
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const activeTab: TabType = (() => {
    const path = location.pathname;
    if (path.startsWith('/sandbox')) return 'sandbox';
    if (path.startsWith('/redux')) return 'redux';
    if (path.startsWith('/graphql')) return 'graphql';
    if (path.startsWith('/expense')) return 'expenseManager';
    return 'dashboard';
  })();

  const userEmail = userData?.me?.email || "Loading...";
  const userName = userData?.me?.name || "Loading...";
  const userInitials = userData?.me?.name
    ? userData.me.name.split(" ").map((n: string) => n[0]).join("").substring(0, 2).toUpperCase()
    : "..";

  const handleSettingsClick = () => {
    navigate("/settings");
  };

  return {
    isSidebarOpen,
    setIsSidebarOpen,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    isDevWorkbenchOpen,
    setIsDevWorkbenchOpen,
    activeTab,
    isCounterInjected,
    setIsCounterInjected,
    reduxStateStr,
    simulatingQuery,
    simulatedPayload,
    simulateGraphQL,
    routes,
    routesLoading,
    theme,
    toggleTheme,
    userEmail,
    userName,
    userInitials,
    userData,
    changeLanguage,
    handleLogout,
    handleSettingsClick,
    currentLanguage: i18n.language || "en"
  };
};
